import React, { useEffect, useState } from 'react';
import { Mail, Loader2, Save, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import api from '../services/api';

/**
 * Customer-facing Email Preferences panel.
 *
 * Currently exposes a single toggle (Bid Notifications). The shape is
 * deliberately a single object so we can drop more channels in later
 * (delivery updates, weekly digest, etc.) without breaking the API contract.
 */
export default function EmailPreferencesPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bidNotifications, setBidNotifications] = useState(true);
  const [original, setOriginal] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get('/users/me/email-prefs');
        if (!active) return;
        const v = !!data.bid_notifications;
        setBidNotifications(v);
        setOriginal(v);
      } catch (e) {
        toast.error('Failed to load email preferences');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const dirty = original !== null && original !== bidNotifications;

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/users/me/email-prefs', {
        bid_notifications: bidNotifications,
      });
      const v = !!data.bid_notifications;
      setBidNotifications(v);
      setOriginal(v);
      toast.success(v ? 'Bid notification emails ON' : 'Bid notification emails OFF');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className="border border-gray-200 shadow-none mb-6"
      data-testid="email-prefs-panel"
    >
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-50 flex items-center justify-center rounded-md flex-shrink-0">
            <Mail className="w-5 h-5 text-blue-700" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold font-['Inter'] text-gray-900">
              Email preferences
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Pick which JustDeliver emails land in your inbox. Push and in-app
              notifications are unaffected.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-3">
            <div
              className={`border rounded-md p-4 ${bidNotifications ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200 bg-white'}`}
              data-testid="email-prefs-bid-row"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-blue-100 flex items-center justify-center rounded-md flex-shrink-0">
                  {bidNotifications ? (
                    <Mail className="w-4 h-4 text-blue-700" strokeWidth={2} />
                  ) : (
                    <BellOff className="w-4 h-4 text-gray-500" strokeWidth={2} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">
                        Bid notifications
                      </p>
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                        Email me when a driver bids on my delivery. Quick bids
                        in the same window are bundled into one email so your
                        inbox stays tidy.
                      </p>
                    </div>
                    <Switch
                      checked={bidNotifications}
                      onCheckedChange={setBidNotifications}
                      data-testid="email-prefs-bid-toggle"
                      aria-label="Receive bid notification emails"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-1">
              <Button
                onClick={handleSave}
                disabled={!dirty || saving}
                className="bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-md gap-2"
                data-testid="email-prefs-save-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                ) : (
                  <><Save className="w-4 h-4" /> Save preferences</>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
